import {useState, useEffect, useCallback, useMemo} from 'react'
import {QueryClient} from '@tanstack/react-query';
import {fetchJson} from '../../../../helpers/fetch.js';

import {
  common_user_select_options_path,
} from '~/routes';

const queryClient = new QueryClient();

export default function useUserOptions({queryKey, propsValue = null, propsLabel, isDisabled, readOnly, reset, excludeUserTypes = []}) {
  const [value, setValue] = useState({value: propsValue, label: propsLabel});

  useEffect(() => {
    setValue({value: propsValue, label: propsLabel});
  }, [propsValue, propsLabel]);

  const fetchOptions = async (inputValue) => {
    if (reset || isDisabled || readOnly) return [];

    const fullUrl = common_user_select_options_path({exclude_user_types: excludeUserTypes, ...(inputValue ? {query: inputValue} : {})});
    const options = await queryClient.fetchQuery({
        queryKey: [fullUrl, queryKey, inputValue],
        queryFn: async () => fetchJson(fullUrl)
    });
    return options.map(([value, label]) => ({value: value, label: label}))
  };

  const debouncedFetch = useMemo(() => {
    let timer;
    let pendingReject;

    return (inputValue) =>
      new Promise((resolve, reject) => {
        if (timer) clearTimeout(timer);
        if (pendingReject) {
          pendingReject({cancelled: true});
          pendingReject = null;
        }
        pendingReject = reject;

        timer = setTimeout(async () => {
          try {
            const result = await fetchOptions(inputValue);
            resolve(result);
          } catch (err) {
            reject(err);
          } finally {
            pendingReject = null;
          }
        }, 400)
      })
  }, [queryClient, queryKey, reset]);

  const loadOptions = useCallback(
    (inputValue) => debouncedFetch(inputValue),
    [debouncedFetch]
  );

  return {value, setValue, loadOptions}
}
